import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { FlexBox } from '../box';
import { styled } from '../styled';
import { WidgetListContext } from '../widget/list';
import { createItemIdMaps, getListDroppableId, getOrCreateItemId, reorderItems, useDragListContext } from './context';
import { SortableItem } from './sortable-item';
export { DragListProvider, reorderItems } from './context';
export const OverlayContainer = styled(FlexBox)(({ theme }) => ({
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[8],
    borderRadius: theme.shape.borderRadius,
    listStyle: 'none',
    display: 'block'
}));
const EmptyContainer = styled(FlexBox)(({ theme }) => ({
    minHeight: theme.spacing(8),
    minWidth: theme.spacing(8)
}));
export const DragList = (props) => {
    const { items, onReorder, renderItem, dragHandleAriaLabel, strategy = verticalListSortingStrategy, withoutDivider = false, withoutHandle = false, listId } = props;
    const context = useDragListContext();
    const { isInProvider } = context;
    const widgetListContext = React.useContext(WidgetListContext);
    const [standaloneActiveId, setActiveId] = React.useState(null);
    const instanceIdRef = React.useRef(listId ?? 'drag-list');
    const itemIdMapsRef = React.useRef(createItemIdMaps());
    const idCounterRef = React.useRef(0);
    const standaloneGetItemId = React.useCallback((item) => getOrCreateItemId(item, itemIdMapsRef.current, idCounterRef, `${instanceIdRef.current}-item`), []);
    const getItemId = isInProvider ? context.getItemId : standaloneGetItemId;
    const itemsWithIds = React.useMemo(() => {
        return items.map((item) => ({
            item,
            id: getItemId(item)
        }));
    }, [items, getItemId]);
    React.useEffect(() => {
        return widgetListContext?.registerWithDragList?.();
    }, [widgetListContext]);
    React.useEffect(() => {
        const currentListId = instanceIdRef.current;
        if (isInProvider) {
            context.registerList({
                listId: currentListId,
                items,
                onReorder,
                renderItem,
                dragHandleAriaLabel,
                withoutDivider,
                withoutHandle,
                widgetListContext: widgetListContext ?? undefined
            });
        }
        return () => {
            if (isInProvider) {
                context.unregisterList(currentListId);
            }
        };
    }, [
        isInProvider,
        items,
        onReorder,
        renderItem,
        dragHandleAriaLabel,
        withoutDivider,
        withoutHandle,
        context,
        widgetListContext
    ]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            reorderItems({
                items,
                itemsWithIds,
                activeId: active.id,
                overId: over.id,
                onReorder
            });
        }
        setActiveId(null);
    };
    const activeItem = itemsWithIds.find((item) => item.id === standaloneActiveId);
    const droppableId = getListDroppableId(instanceIdRef.current);
    const { setNodeRef } = useDroppable({
        id: droppableId,
        disabled: !isInProvider
    });
    const sortableContent = (_jsx(SortableContext, { items: itemsWithIds.map((item) => item.id), strategy: strategy, children: itemsWithIds.map(({ item, id }) => (_jsx(SortableItem, { id: id, item: item, renderItem: renderItem, dragHandleAriaLabel: dragHandleAriaLabel, withoutDivider: withoutDivider, withoutHandle: withoutHandle }, id))) }));
    if (isInProvider) {
        if (items.length === 0) {
            return _jsx(EmptyContainer, { ref: setNodeRef });
        }
        return sortableContent;
    }
    const overlayContent = activeItem ? (_jsx(OverlayContainer, { children: _jsx(SortableItem, { id: activeItem.id, item: activeItem.item, isOverlay: true, renderItem: renderItem, dragHandleAriaLabel: dragHandleAriaLabel, withoutDivider: withoutDivider, withoutHandle: withoutHandle }) })) : null;
    const wrappedOverlayContent = overlayContent && widgetListContext ? (_jsx(WidgetListContext.Provider, { value: widgetListContext, children: overlayContent })) : (overlayContent);
    return (_jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragEnd: handleDragEnd, children: [sortableContent, createPortal(_jsx(DragOverlay, { children: wrappedOverlayContent }), document.body)] }));
};
